import { Button, Empty, Tooltip } from '@maxtropy/components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LeftOutlined,
  RightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import styles from './index.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiV2AreaCategoryScadaCategoryPost } from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';
import { cancelFullscreen, goFullscreen } from '../EnergyOverview/utils';
import ScadaGraphPage from '../ScadaGraphPage';
import caretLeftFilled from './imgs/caret-left-filled.png';
import caretRightFilled from './imgs/caret-right-filled.png';

const StationScadaBuilding: React.FC = () => {
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const baseDivRef = useRef<HTMLDivElement>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryScrollContainerRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const [showCategoryArrows, setShowCategoryArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canCategoryScrollLeft, setCanCategoryScrollLeft] = useState(false);
  const [canCategoryScrollRight, setCanCategoryScrollRight] = useState(false);
  const location = useLocation();
  const { search } = location;
  const urlSearchParams = new URLSearchParams(search);
  const url_stationId = urlSearchParams.get('stationId') || undefined;
  const parsedUrlStationId = useMemo(() => {
    if (!url_stationId) return undefined;
    const parsed = Number(url_stationId);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [url_stationId]);
  const [categoryOptions, setCategoryOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number>();
  const [categoryData, setCategoryData] = useState<any[]>([]);

  useEffect(() => {
    // 获取站房分组列表
    apiV2AreaCategoryScadaCategoryPost({}).then(res => {
      const list = res.list || [];
      setCategoryData(list);

      // 设置分类列表
      const categoryOpts = list.map((item: any) => ({
        value: item.id,
        label: item.description || `分类${item.id}`,
      }));
      setCategoryOptions(categoryOpts);

      // 检查分类是否需要显示箭头
      if (categoryOpts.length > 4) {
        setShowCategoryArrows(true);
        setTimeout(() => {
          checkCategoryScrollState();
        }, 50);
      } else {
        setShowCategoryArrows(false);
      }
    });
  }, []);

  useEffect(() => {
    if (categoryData.length === 0) return;

    const matchedCategory = parsedUrlStationId
      ? categoryData.find(category =>
          (category.list || []).some((station: any) => Number(station.id) === parsedUrlStationId)
        )
      : undefined;
    const nextCategoryId = matchedCategory?.id ?? categoryData[0]?.id;

    if (nextCategoryId && nextCategoryId !== selectedCategory) {
      setSelectedCategory(nextCategoryId);
    }
  }, [categoryData, parsedUrlStationId, selectedCategory]);

  const stationList = useMemo(() => {
    if (!selectedCategory || categoryData.length === 0) return [];

    const currentCategory = categoryData.find(item => item.id === selectedCategory);
    const stations = (currentCategory?.list || []).filter((station: any) => station.id != null);

    return stations.map((station: any) => ({
      value: station.areaId,
      scadaId: Number(station.id),
      label: station.name,
      needMeter: station.needMeter,
    }));
  }, [categoryData, selectedCategory]);

  const currentStationId = useMemo(() => {
    return (
      stationList.find((station: any) => station.scadaId === parsedUrlStationId)?.scadaId ?? stationList[0]?.scadaId
    );
  }, [parsedUrlStationId, stationList]);

  const isUrlStationIdValid = useMemo(() => {
    if (isNil(parsedUrlStationId)) return false;
    return categoryData.some(category =>
      (category.list || []).some((station: any) => Number(station.id) === parsedUrlStationId)
    );
  }, [categoryData, parsedUrlStationId]);

  useEffect(() => {
    if (!currentStationId || currentStationId === parsedUrlStationId) return;
    // 仅在 URL 缺少 stationId 或 stationId 无效时回填，避免和用户主动切换相互覆盖
    if (!isNil(parsedUrlStationId) && isUrlStationIdValid) return;
    navigate(`?stationId=${currentStationId}`, { replace: true });
  }, [currentStationId, isUrlStationIdValid, navigate, parsedUrlStationId]);

  // 检查站房是否需要显示箭头
  useEffect(() => {
    if (stationList && stationList.length > 4) {
      setShowArrows(true);
      setTimeout(() => {
        checkScrollState();
      }, 50);
    } else {
      setShowArrows(false);
    }
  }, [stationList]);

  // 站房列表变化时，自动滚动到选中的站房
  useEffect(() => {
    if (currentStationId && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeBtn = container.querySelector(`.${styles.active}`) as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentStationId, stationList]);

  // 分类列表变化时，自动滚动到选中的分类
  useEffect(() => {
    if (selectedCategory && categoryScrollContainerRef.current) {
      const container = categoryScrollContainerRef.current;
      const activeNav = container.querySelector(`.${styles.activeNav}`) as HTMLElement;
      if (activeNav) {
        activeNav.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedCategory, categoryOptions]);

  const checkScrollState = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
    }
  };

  const checkCategoryScrollState = () => {
    const container = categoryScrollContainerRef.current;
    if (container) {
      setCanCategoryScrollLeft(container.scrollLeft > 0);
      setCanCategoryScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
    }
  };

  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollState, 300);
    }
  };

  const handleCategoryScroll = (direction: 'left' | 'right') => {
    const container = categoryScrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -135 : 135; // 160px - 25px 重叠
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkCategoryScrollState, 300);
    }
  };

  const stationName = useMemo(() => {
    return stationList?.find((k: any) => k.scadaId === currentStationId)?.label;
  }, [currentStationId, stationList]);

  const currentScadaId = useMemo(() => {
    return currentStationId;
  }, [currentStationId]);

  const currentNeedMeter = useMemo(() => {
    return stationList?.find((k: any) => k.scadaId === currentStationId)?.needMeter;
  }, [currentStationId, stationList]);

  const currentAreaId = useMemo(() => {
    return stationList?.find((k: any) => k.scadaId === currentStationId)?.value;
  }, [currentStationId, stationList]);

  const handleFullScreen = () => {
    setIsFullScreen(document.fullscreenElement !== null);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreen);
    return () => document.removeEventListener('fullscreenchange', handleFullScreen);
  }, []);

  const fullBtn = () => {
    if (baseDivRef.current) {
      isFullScreen ? cancelFullscreen() : goFullscreen(baseDivRef.current);
    }
  };

  return (
    <div
      ref={ref => {
        if (ref) {
          baseDivRef.current = ref;
        }
      }}
    >
      <div className={styles.header_area_box}>
        <div className={styles.title_box}>
          <Tooltip title={stationName}>
            <span className={styles.text}>{stationName}</span>
          </Tooltip>
        </div>
      </div>
      <div className={styles.content_area_box}>
        <div className={styles.category_buttons}>
          {showCategoryArrows && (
            <img
              src={caretLeftFilled}
              alt="left"
              className={`${styles.scroll_btn} ${!canCategoryScrollLeft ? styles.disabled : ''}`}
              onClick={() => handleCategoryScroll('left')}
              style={{
                cursor: canCategoryScrollLeft ? 'pointer' : 'not-allowed',
                opacity: canCategoryScrollLeft ? 1 : 0.3,
              }}
            />
          )}
          <div className={styles.nav_list} ref={categoryScrollContainerRef} onScroll={checkCategoryScrollState}>
            {categoryOptions.map(item => (
              <Tooltip title={item.label}>
                <div
                  key={item.value}
                  className={`${styles.nav} ${selectedCategory === item.value ? styles.activeNav : ''}`}
                  onClick={() => {
                    setSelectedCategory(item.value);
                    const nextCategory = categoryData.find(category => category.id === item.value);
                    const nextStationId = (nextCategory?.list || []).find((station: any) => station.id != null)?.id;
                    if (nextStationId) {
                      navigate(`?stationId=${nextStationId}`, { replace: true });
                    }
                  }}
                >
                  {item.label && item.label.length > 6 ? `${item.label.slice(0, 6)}...` : item.label}
                </div>
              </Tooltip>
            ))}
          </div>
          {showCategoryArrows && (
            <img
              src={caretRightFilled}
              alt="right"
              className={`${styles.scroll_btn} ${!canCategoryScrollRight ? styles.disabled : ''}`}
              onClick={() => handleCategoryScroll('right')}
              style={{
                cursor: canCategoryScrollRight ? 'pointer' : 'not-allowed',
                opacity: canCategoryScrollRight ? 1 : 0.3,
              }}
            />
          )}
        </div>
        <div className={styles.station_buttons}>
          {showArrows && (
            <Button
              className={`${styles.scroll_btn} ${!canScrollLeft ? styles.disabled : ''}`}
              onClick={() => handleScroll('left')}
              disabled={!canScrollLeft}
            >
              <LeftOutlined />
            </Button>
          )}
          <div className={styles.station_list} ref={scrollContainerRef} onScroll={checkScrollState}>
            {stationList.map((item: any) => (
              <Button
                key={item.value}
                type="default"
                className={`${styles.station_btn} ${currentStationId === item.scadaId ? styles.active : ''}`}
                onClick={() => {
                  navigate(`?stationId=${item.scadaId}`, { replace: true });
                }}
              >
                <Tooltip title={item.label}>
                  <span className={styles.station_name}>
                    {item.label?.length > 6 ? `${item.label.slice(0, 6)}...` : item.label}
                  </span>
                </Tooltip>
              </Button>
            ))}
          </div>
          {showArrows && (
            <Button
              className={`${styles.scroll_btn} ${!canScrollRight ? styles.disabled : ''}`}
              onClick={() => handleScroll('right')}
              disabled={!canScrollRight}
            >
              <RightOutlined />
            </Button>
          )}
        </div>
        <div className={styles.btns}>
          <div className={styles.btn_row}>
            <div
              className={styles.btn}
              onClick={() => {
                navigate('/station/scada/building/config');
              }}
            >
              <ToolOutlined />
              &nbsp;
              <span className={styles.text}>配置</span>
              <div className={styles.bottomLine} />
            </div>

            <div className={styles.btn} onClick={fullBtn}>
              {isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              &nbsp;
              <span className={styles.text}>{isFullScreen ? '退出' : '全屏'}</span>
              <div className={styles.bottomLine} />
            </div>
          </div>

          <div>
            {currentNeedMeter && (
              <Button
                type="primary"
                onClick={() => window.open(`/station/meter/reading?id=${currentAreaId}`, '_blank')}
              >
                抄表数据
              </Button>
            )}
          </div>
        </div>
        {isNil(currentScadaId) ? (
          <div
            style={{
              width: '100%',
              height: isFullScreen ? 'calc(100vh - 50px)' : 'calc(100vh - 100px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Empty />
          </div>
        ) : (
          <ScadaGraphPage
            key={currentScadaId}
            styleHeight={isFullScreen ? 'calc(100vh - 50px)' : 'calc(100vh - 100px)'}
            scadaId={currentScadaId}
            backgroundColor="transparent"
          />
        )}
      </div>
    </div>
  );
};

export default StationScadaBuilding;
